import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { materialsApi } from '@/api/materials'
import { Button } from '@/components/ui/button'
import { showError } from '@/lib/error'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { MaterialFolder } from '@/types/material'

interface MaterialFolderDialogProps {
	open: boolean
	parent?: MaterialFolder
	current?: MaterialFolder
	onOpenChange: (open: boolean) => void
}

export function MaterialFolderDialog({
	open,
	parent,
	current,
	onOpenChange,
}: MaterialFolderDialogProps) {
	const queryClient = useQueryClient()
	const [name, setName] = useState('')

	useEffect(() => {
		if (!open) return
		setName(current?.name ?? '')
	}, [open, current])

	const saveMutation = useMutation({
		mutationFn: (value: string) => current
			? materialsApi.updateFolder(current.id, { name: value })
			: materialsApi.createFolder({ parent_id: parent?.id ?? 0, name: value }),
		onSuccess: () => {
			toast.success(current ? '目录已重命名' : '目录已创建')
			queryClient.invalidateQueries({ queryKey: ['material-folders'] })
			onOpenChange(false)
		},
		onError: (error) => showError(error, current ? '重命名目录失败' : '创建目录失败'),
	})

	const handleSubmit = () => {
		const trimmedName = name.trim()
		if (!trimmedName) {
			toast.error('请输入目录名称')
			return
		}
		saveMutation.mutate(trimmedName)
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{current ? '重命名目录' : '新增目录'}</DialogTitle>
					<DialogDescription>
						{current ? '更新素材目录名称' : `创建在：${parent?.name ?? '根目录'}`}
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-2">
					<Label htmlFor="material-folder-name">目录名称</Label>
					<Input
						id="material-folder-name"
						value={name}
						onChange={(event) => setName(event.target.value)}
						maxLength={30}
					/>
				</div>
				<DialogFooter>
					<Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saveMutation.isPending}>
						取消
					</Button>
					<Button type="button" onClick={handleSubmit} disabled={saveMutation.isPending}>
						保存
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
